import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import siteMessage from "@insite/client-framework/SiteMessage";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Typography from "@insite/mobius/Typography";
import parse from "html-react-parser";
import * as React from "react";
import { useContext } from "react";
import { css } from "styled-components";

export interface InvoiceDetailsShipmentPackagesStyles {
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    shipmentDateHeader?: DataTableHeaderProps;
    carrierHeader?: DataTableHeaderProps;
    shipViaHeader?: DataTableHeaderProps;
    trackingNumberHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    shipmentDateCell?: DataTableCellProps;
    carrierCell?: DataTableCellProps;
    shipViaCell?: DataTableCellProps;
    trackingNumberCell?: DataTableCellProps;
}

export interface CustomShipment {
    carrier: string;
    shipVia: string;
    trackingNumber: string;
    shipmentId: string;
    shipDate: string;
    trackingUrl: string;
}

export const shipmentPackagesStyles: InvoiceDetailsShipmentPackagesStyles = {
    shipmentDateCell: {
        css: css`
            overflow: auto;
            max-width: 250px;
        `,
    },
};

const styles = shipmentPackagesStyles;

const InvoiceDetailsShipmentPackages: React.FC = () => {
    const { value: invoice } = useContext(InvoiceStateContext);

    if (!invoice) {
        return null;
    }

    const shipmentPackages: CustomShipment[] = [];

    if (invoice && invoice.properties) {
        const shipmentsCount = Object.keys(invoice.properties).filter(key =>
            key.startsWith("shipment_ShipmentId_"),
        ).length;

        for (let i = 0; i < shipmentsCount; i++) {
            shipmentPackages.push({
                shipmentId: invoice.properties[`shipment_ShipmentId_${i}`],
                shipDate: invoice.properties[`shipment_ShipDate_${i}`],
                carrier: invoice.properties[`shipment_Carrier_${i}`],
                shipVia: invoice.properties[`shipment_ShipVia_${i}`],
                trackingNumber: invoice.properties[`shipment_TrackingNumber_${i}`],
                trackingUrl: invoice.properties[`shipment_TrackingUrl_${i}`],
            });
        }
    }

    if (!shipmentPackages || shipmentPackages.length === 0) {
        return null;
    }

    const checkIfDateIsValid = (date: Date) => {
        const fromDate = new Date("01/01/2010");
        const toDate = new Date("01/01/2100");

        if (date <= toDate && date >= fromDate) {
            return true;
        }
        return false;
    };

    return (
        <DataTable {...styles.dataTable}>
            <DataTableHead {...styles.dataTableHead}>
                <DataTableHeader {...styles.shipmentDateHeader}>{translate("Ship Date")}</DataTableHeader>
                <DataTableHeader {...styles.carrierHeader}>{translate("Carrier")}</DataTableHeader>
                <DataTableHeader {...styles.shipViaHeader}>{translate("Service")}</DataTableHeader>
                <DataTableHeader {...styles.trackingNumberHeader}>{translate("Tracking #")}</DataTableHeader>
            </DataTableHead>
            <DataTableBody {...styles.dataTableBody}>
                {shipmentPackages &&
                    shipmentPackages.length > 0 &&
                    shipmentPackages.map(shipmentPackage => (
                        <DataTableRow {...styles.dataTableRow} key={shipmentPackage.shipmentId}>
                            <DataTableCell {...styles.shipmentDateCell}>
                                {checkIfDateIsValid(new Date(shipmentPackage.shipDate)) && (
                                    <LocalizedDateTime
                                        dateTime={new Date(shipmentPackage.shipDate)}
                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                    />
                                )}
                                {!checkIfDateIsValid(new Date(shipmentPackage.shipDate)) && (
                                    <Typography>{siteMessage("ShipmentDateInfo")}</Typography>
                                )}
                            </DataTableCell>
                            <DataTableCell {...styles.carrierCell}>{shipmentPackage.carrier}</DataTableCell>
                            <DataTableCell {...styles.shipViaCell}>{shipmentPackage.shipVia}</DataTableCell>
                            <DataTableCell
                                {...styles.trackingNumberCell}
                                data-test-selector="orderDetails_TrackingNumber"
                            >
                                {shipmentPackage.trackingUrl !== undefined &&
                                    parse(
                                        `<a href='${shipmentPackage.trackingUrl}' target="_blank">
                                        ${shipmentPackage.trackingNumber}
                                    </a>`,
                                        parserOptions,
                                    )}
                                {shipmentPackage.trackingUrl === undefined && shipmentPackage.trackingNumber}
                            </DataTableCell>
                        </DataTableRow>
                    ))}
            </DataTableBody>
        </DataTable>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceDetailsShipmentPackages,
    definition: {
        allowedContexts: ["InvoiceDetailsPage"],
        displayName: "Invoice Package Details",
        group: "Invoice History",
    },
};

export default widgetModule;
